import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { PlusIcon, SearchIcon, EditIcon, EyeIcon, FilterIcon, RefreshCwIcon, AlertCircleIcon, Stethoscope, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';
import { doctorsApi, Doctor, DoctorsQueryParams } from '../../lib/api';
import AddDoctorModal from '../../components/Doctors/AddDoctorModal';
import EditDoctorModal from '../../components/Doctors/EditDoctorModal';
import DoctorDetailsModal from '../../components/Doctors/DoctorDetailsModal';
import ScheduleManagementModal from '../../components/Doctors/ScheduleManagementModal';

const DoctorsPage = () => {
  // Get current user context
  const { user: currentUser } = useSelector((state: RootState) => state.auth);
  
  // Check if current user is admin (only admins can edit doctors)
  const isAdmin = currentUser?.role?.toUpperCase() === 'ADMIN';
  const canEdit = isAdmin;

  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Filters and search
  const [searchTerm, setSearchTerm] = useState('');
  const [specializationFilter, setSpecializationFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedDoctorId, setSelectedDoctorId] = useState<string | null>(null);
  const [selectedDoctor, setSelectedDoctor] = useState<Doctor | null>(null);

  // Fetch doctors with current filters and pagination
  const fetchDoctors = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params: DoctorsQueryParams = {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm || undefined,
        specialization: specializationFilter || undefined,
        department: departmentFilter || undefined,
        sortBy,
        sortOrder
      };

             const response = await doctorsApi.getDoctors(params);
       
       if (response.data.success && response.data.data) {
         setDoctors(response.data.data.doctors);
         setTotalPages(response.data.data.pagination.totalPages);
         setTotalItems(response.data.data.pagination.totalItems);
       } else {
         setError('Failed to fetch doctors');
       }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch doctors');
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchTerm, specializationFilter, departmentFilter, sortBy, sortOrder]);

  useEffect(() => {
    fetchDoctors();
  }, [fetchDoctors]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchDoctors();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRefresh = () => {
    setCurrentPage(1);
    fetchDoctors();
    toast.success('Doctors list refreshed');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatSpecialization = (specialization: string) => {
    return specialization.replace(/([A-Z])/g, ' $1').trim();
  };

  const specializationOptions = [
    'Cardiology',
    'Neurology',
    'Orthopedics',
    'Pediatrics',
    'Dermatology',
    'Psychiatry',
    'Radiology',
    'Emergency Medicine',
    'Internal Medicine',
    'Surgery'
  ];

  const departmentOptions = [
    'Internal Medicine',
    'Surgery',
    'Pediatrics',
    'Emergency',
    'Radiology',
    'Laboratory',
    'Neuroscience',
    'Cardiology',
    'Orthopedics',
    'Psychiatry'
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Doctors</h1>
          <p className="mt-1 text-sm text-gray-500">
            {isAdmin 
              ? 'Manage hospital doctors, specializations, and schedules'
              : 'View hospital doctors, specializations, and schedules'
            }
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-3">
          <button 
            onClick={handleRefresh}
            className="btn-secondary"
            disabled={loading}
          >
            <RefreshCwIcon className="h-4 w-4" />
          </button>
          {canEdit && (
            <button 
              onClick={() => setShowAddModal(true)}
              className="btn-primary w-full sm:w-auto"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Doctor
            </button>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
          {/* Search */}
          <div className="flex-1 max-w-lg">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search doctors by name, email, license number..."
                className="input pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn-secondary lg:w-auto"
          >
            <FilterIcon className="h-4 w-4 mr-2" />
            Filters
            {(specializationFilter || departmentFilter) && (
              <span className="ml-2 bg-primary-100 text-primary-800 text-xs px-2 py-1 rounded-full">
                {[specializationFilter, departmentFilter].filter(Boolean).length}
              </span>
            )}
          </button>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="form-label">Specialization</label>
                <select
                  className="input"
                  value={specializationFilter}
                  onChange={(e) => setSpecializationFilter(e.target.value)}
                >
                  <option value="">All Specializations</option>
                  {specializationOptions.map(spec => (
                    <option key={spec} value={spec}>{spec}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="form-label">Department</label>
                <select
                  className="input"
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                >
                  <option value="">All Departments</option>
                  {departmentOptions.map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="form-label">Sort by</label>
                <select
                  className="input"
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-');
                    setSortBy(field);
                    setSortOrder(order as 'asc' | 'desc');
                  }}
                >
                  <option value="createdAt-desc">Newest First</option>
                  <option value="createdAt-asc">Oldest First</option>
                  <option value="user.firstName-asc">Name A-Z</option>
                  <option value="user.firstName-desc">Name Z-A</option>
                  <option value="specialization-asc">Specialization A-Z</option>
                  <option value="consultationFee-desc">Fee High-Low</option>
                  <option value="consultationFee-asc">Fee Low-High</option>
                </select>
              </div>
            </div>

            {(specializationFilter || departmentFilter) && (
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => {
                    setSpecializationFilter('');
                    setDepartmentFilter('');
                  }}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Results Summary */}
      {!loading && (
        <div className="text-sm text-gray-600">
          Showing {doctors.length} of {totalItems} doctors
          {searchTerm && ` matching "${searchTerm}"`}
          {specializationFilter && ` in ${specializationFilter}`}
          {departmentFilter && ` from ${departmentFilter} department`}
        </div>
      )}

      {/* Doctors Table - Desktop */}
      <div className="hidden lg:block bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8">
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse flex space-x-4">
                  <div className="rounded-full bg-gray-200 h-12 w-12"></div>
                  <div className="flex-1 space-y-2 py-1">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <AlertCircleIcon className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <div className="text-red-500 mb-2">Error loading doctors</div>
            <p className="text-gray-600 mb-4">{error}</p>
            <button onClick={fetchDoctors} className="btn-primary">
              Try Again
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead className="table-header">
                <tr>
                  <th className="table-header-cell">Doctor</th>
                  <th className="table-header-cell">Contact</th>
                  <th className="table-header-cell">Specialization</th>
                  <th className="table-header-cell">Department</th>
                  <th className="table-header-cell">Consultation Fee</th>
                  <th className="table-header-cell">Appointments</th>
                  <th className="table-header-cell">Status</th>
                  <th className="table-header-cell">Actions</th>
                </tr>
              </thead>
            <tbody className="table-body">
              {doctors.length === 0 ? (
                <tr>
                  <td colSpan={8} className="table-cell text-center py-8">
                    <div className="text-gray-500">
                      {searchTerm || specializationFilter || departmentFilter ? 
                        'No doctors found matching your search criteria.' : 
                        'No doctors registered yet.'
                      }
                    </div>
                  </td>
                </tr>
              ) : (
                doctors.map((doctor) => (
                  <tr key={doctor.id}>
                    <td className="table-cell">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                          <Stethoscope className="h-5 w-5 text-primary-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            Dr. {doctor.user.firstName} {doctor.user.lastName}
                          </div>
                          <div className="text-sm text-gray-500">
                            License: {doctor.licenseNumber}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="text-sm text-gray-900">{doctor.user.email}</div>
                      <div className="text-sm text-gray-500">{doctor.user.phone}</div>
                    </td>
                    <td className="table-cell">
                      <span className="badge badge-primary">
                        {formatSpecialization(doctor.specialization)}
                      </span>
                    </td>
                    <td className="table-cell">
                      <div className="text-sm text-gray-900">{doctor.department}</div>
                    </td>
                    <td className="table-cell">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(doctor.consultationFee)}
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="text-sm text-gray-900">
                        {doctor._count?.appointments || 0} appointments
                      </div>
                    </td>
                    <td className="table-cell">
                      <span className={`badge ${
                        doctor.user.isActive ? 'badge-success' : 'badge-gray'
                      }`}>
                        {doctor.user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={() => {
                            setSelectedDoctorId(doctor.id);
                            setShowDetailsModal(true);
                          }}
                          className="p-1 text-gray-400 hover:text-gray-500"
                          title="View doctor details"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        {canEdit && (
                          <button 
                            onClick={() => {
                              setSelectedDoctor(doctor);
                              setShowEditModal(true);
                            }}
                            className="p-1 text-gray-400 hover:text-gray-500"
                            title="Edit doctor"
                          >
                            <EditIcon className="h-4 w-4" />
                          </button>
                        )}
                        <button 
                          onClick={() => {
                            setSelectedDoctor(doctor);
                            setShowScheduleModal(true);
                          }}
                          className="p-1 text-gray-400 hover:text-gray-500"
                          title="Manage schedule"
                        >
                          <Calendar className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button 
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button 
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{((currentPage - 1) * itemsPerPage) + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalItems)}</span> of{' '}
                  <span className="font-medium">{totalItems}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button 
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="btn-secondary rounded-l-md disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button className="btn-primary">{currentPage}</button>
                  <button 
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="btn-secondary rounded-r-md disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Doctors Cards - Mobile */}
      <div className="lg:hidden space-y-4">
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow p-4 animate-pulse">
                <div className="flex items-start space-x-3">
                  <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <AlertCircleIcon className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <div className="text-red-500 mb-2">Error loading doctors</div>
            <p className="text-gray-600 mb-4">{error}</p>
            <button onClick={fetchDoctors} className="btn-primary">
              Try Again
            </button>
          </div>
        ) : doctors.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="text-gray-500">
              {searchTerm || specializationFilter || departmentFilter ? 
                'No doctors found matching your search criteria.' : 
                'No doctors registered yet.'
              }
            </div>
          </div>
        ) : (
          doctors.map((doctor) => (
            <div key={doctor.id} className="bg-white rounded-lg shadow p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                    <Stethoscope className="h-5 w-5 text-primary-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-medium text-gray-900 truncate">
                      Dr. {doctor.user.firstName} {doctor.user.lastName}
                    </h3>
                    <p className="text-sm text-gray-500">License: {doctor.licenseNumber}</p>
                    
                    <div className="mt-2 space-y-1">
                      <p className="text-sm text-gray-700">{doctor.user.email}</p>
                      <p className="text-sm text-gray-500">{doctor.user.phone}</p>
                    </div>
                    
                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className="badge badge-primary">
                        {formatSpecialization(doctor.specialization)}
                      </span>
                      <span className="badge badge-secondary">
                        {doctor.department}
                      </span>
                      <span className={`badge ${
                        doctor.user.isActive ? 'badge-success' : 'badge-gray'
                      }`}>
                        {doctor.user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    
                    <div className="mt-2 text-sm text-gray-900 font-medium">
                      Consultation Fee: {formatCurrency(doctor.consultationFee)}
                    </div>
                    
                    <div className="mt-1 text-xs text-gray-500">
                      {doctor._count?.appointments || 0} appointments
                    </div>
                  </div>
                </div>
                
                <div className="flex space-x-2 ml-4">
                  <button 
                    onClick={() => {
                      setSelectedDoctorId(doctor.id);
                      setShowDetailsModal(true);
                    }}
                    className="p-2 text-gray-400 hover:text-gray-500"
                    title="View doctor details"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </button>
                  {canEdit && (
                    <button 
                      onClick={() => {
                        setSelectedDoctor(doctor);
                        setShowEditModal(true);
                      }}
                      className="p-2 text-gray-400 hover:text-gray-500"
                      title="Edit doctor"
                    >
                      <EditIcon className="h-4 w-4" />
                    </button>
                  )}
                  <button 
                    onClick={() => {
                      setSelectedDoctor(doctor);
                      setShowScheduleModal(true);
                    }}
                    className="p-2 text-gray-400 hover:text-gray-500"
                    title="Manage schedule"
                  >
                    <Calendar className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Doctor Modal */}
      <AddDoctorModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onDoctorAdded={() => {
          setShowAddModal(false);
          fetchDoctors();
        }}
      />

      {/* Edit Doctor Modal */}
      <EditDoctorModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onDoctorUpdated={() => {
          setShowEditModal(false);
          fetchDoctors();
        }}
        doctor={selectedDoctor}
      />

      {/* Doctor Details Modal */}
      <DoctorDetailsModal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        doctorId={selectedDoctorId}
      />

      {/* Schedule Management Modal */}
      <ScheduleManagementModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        doctorId={selectedDoctor?.id || ''}
        doctorName={selectedDoctor ? `Dr. ${selectedDoctor.user.firstName} ${selectedDoctor.user.lastName}` : ''}
      />
    </div>
  );
};

export default DoctorsPage; 